import { createFileRoute, isNotFound, Link, useRouteContext } from '@tanstack/react-router';
import { buttonVariants } from '@worldcoo/ui/components/button';
import { RadioGroup, RadioGroupItem } from '@worldcoo/ui/components/radio-group';
import { useLingui } from '@lingui/react/macro';
import { donationAmountSchema } from '@/lib/donation-amount-schema';
import { cn, formatNumber } from '@worldcoo/ui/lib/utils';
import { Title } from '@worldcoo/ui/components/form/title';
import { Paragraph } from '@worldcoo/ui/components/form/paragraph';
import { useAppForm } from '@/lib/use-app-form';
import { Label } from '@worldcoo/ui/components/label';
import { Animate } from '@worldcoo/ui/custom/animate';
import { usePersistedStore } from '@/lib/app-store';
import { usePortalCampaignQuery } from '@/lib/data-hooks';
import { currencySymbol } from '@/lib/currency-symbol';
import { fieldErrorMessage } from '@worldcoo/ui/components/form/shared';
import { localeToLanguage } from '@worldcoo/shared/core';
import { useState } from 'react';
import { AmountInput, amountToDisplay } from '@worldcoo/ui/components/amount-input';
import { HeartIcon } from './-components/assets/heart-icon';
import { DonateStepPending } from './-components/donate-step-pending';

export const Route = createFileRoute('/$portalId/$campaignId/donate/')({
	head: () => ({ meta: [{ name: 'robots', content: 'noindex' }] }),
	component: Component,
	pendingComponent: DonateStepPending,
});

function Component() {
	const { portalId, campaignId } = Route.useParams();
	const navigate = Route.useNavigate();
	const { t, i18n } = useLingui();
	const { locale } = useRouteContext({ from: '__root__' });
	const amount = usePersistedStore((state) => state.amount);
	const setAmount = usePersistedStore((state) => state.setAmount);
	const campaign = usePortalCampaignQuery(campaignId);

	const langCode = localeToLanguage[locale];

	const donationAmounts =
		!isNotFound(campaign) && campaign.data.donationAmounts.length > 0 ? campaign.data.donationAmounts : [5, 10, 25, 50];
	const donationEquivalents = !isNotFound(campaign) ? campaign.data.donationEquivalents : [];
	const currencyCode = !isNotFound(campaign) ? campaign.data.currencyCode : 'EUR';
	const symbol = currencySymbol(locale, currencyCode);

	const initialAmount = amount ?? donationAmounts[1] ?? 10;
	// A raw locale string ("12,50") beside the number: bound to the number alone, decimals cannot be typed.
	const [isCustomInput, setIsCustomInput] = useState(!donationAmounts.includes(initialAmount));
	const [customDisplay, setCustomDisplay] = useState(() =>
		donationAmounts.includes(initialAmount) ? '' : amountToDisplay(locale, initialAmount),
	);

	const form = useAppForm({
		validators: { onChange: donationAmountSchema(i18n, locale, currencyCode) },
		defaultValues: { amount: initialAmount },
		onSubmit: ({ value }) => {
			setAmount(value.amount);
			navigate({ to: `/$portalId/$campaignId/donate/details`, params: { portalId, campaignId } });
		},
	});

	return (
		<Animate>
			<form
				onSubmit={(event) => {
					event.preventDefault();
					form.handleSubmit();
				}}
				className="flex h-full grow flex-col gap-6"
			>
				<div className="flex flex-col gap-3">
					<Title size="lg" className="text-center">
						{t`Make your donation`}
					</Title>
					<Paragraph className="text-center" as="div">
						<CampaignText />
					</Paragraph>
				</div>

				<form.AppField
					name="amount"
					children={(field) => {
						const isPreset = !isCustomInput && donationAmounts.includes(field.state.value);
						const selectedIndex = isPreset ? donationAmounts.indexOf(field.state.value) : -1;
						const equivalentText = selectedIndex >= 0 ? donationEquivalents[selectedIndex]?.[langCode] : undefined;

						return (
							<>
								{equivalentText && (
									<div className="flex items-center justify-center gap-2 rounded-md bg-[#f5f5f5] p-2">
										<HeartIcon className="text-primary size-6 shrink-0" />
										<p className="text-sm tracking-tight">{equivalentText}</p>
									</div>
								)}

								<RadioGroup
									onValueChange={(value) => {
										setIsCustomInput(false);
										setCustomDisplay('');
										field.handleChange(Number(value));
									}}
									value={isPreset ? field.state.value.toString() : ''}
									className="grid grid-cols-2 gap-2"
								>
									{donationAmounts.map((da) => {
										const val = da.toString();
										const isSelected = isPreset && field.state.value === da;
										return (
											<div key={val} className="flex">
												<RadioGroupItem value={val} id={`amount-${val}`} className="peer sr-only absolute" />
												<Label
													htmlFor={`amount-${val}`}
													className={cn(
														buttonVariants({
															variant: 'outline-dark',
															size: 'xl',
														}),
														// 75ms ease-out is past half on frame one (the base's 150ms is 3%). bg-clip-border: padding-box shows a
														// seam between border and fill, hence the selected state's transparent border.
														'w-full bg-clip-border transition-colors duration-75 ease-out pointer-coarse:transition-none active:scale-[0.98]',
														isSelected && 'bg-primary border-transparent text-primary-foreground hover:bg-primary/70',
													)}
												>
													{formatNumber(locale, da, Number.isInteger(da) ? 0 : 2)} {symbol}
												</Label>
											</div>
										);
									})}
								</RadioGroup>

								<div className="grow">
									<AmountInput
										locale={locale}
										suffix={symbol}
										className={cn(
											buttonVariants({
												variant: isCustomInput && customDisplay !== '' ? 'default' : 'outline-dark',
												size: 'xl',
											}),
											'h-[58px] w-full text-[20px]',
										)}
										placeholder={t`Other amount`}
										value={isCustomInput ? customDisplay : ''}
										errorMessage={fieldErrorMessage(field.state)}
										onValueChange={(display, value) => {
											setIsCustomInput(true);
											setCustomDisplay(display);
											field.handleChange(value);
										}}
									/>
								</div>
							</>
						);
					}}
				/>

				<Link
					to="/$portalId/$campaignId/donate/details"
					params={{ portalId, campaignId }}
					className={cn(buttonVariants({ size: '2xl' }), 'mt-6 w-full')}
					onClick={(e) => {
						// The form's onSubmit navigates; the Link only when already valid.
						if (!form.state.isValid) {
							e.preventDefault();
							form.handleSubmit();
							return;
						}
						setAmount(form.state.values.amount);
					}}
				>
					{t`Next`}
				</Link>
			</form>
		</Animate>
	);
}

function CampaignText() {
	const { campaignId } = Route.useParams();
	const campaign = usePortalCampaignQuery(campaignId);

	if (isNotFound(campaign)) return null;

	return (
		<>
			{campaign.data.texts.objectives && <div dangerouslySetInnerHTML={{ __html: campaign.data.texts.objectives }} />}
		</>
	);
}
